import React, { CSSProperties, ReactNode, forwardRef } from 'react';
import { Button as BaseButton } from '@mui/base';
import { MUIStyledCommonProps, styled } from '@mui/system';
import clsx from 'clsx';

interface ButtonProps {
  type?: 'primary' | 'secondary' | 'outline' | 'info' | 'white' | 'link';
  size?: 'large' | 'medium' | 'small' | 'mini';
  label?: string;
  onClick?: () => void;
  disabled?: boolean;
  startIconName?: string;
  startIconSize?: '8' | '16' | '24' | '32';
  startIconNode?: ReactNode;
  endIconName?: string;
  endIconSize?: '8' | '16' | '24' | '32';
  endIconNode?: ReactNode;
  children?: ReactNode;
  styles?: CSSProperties;
  className?: string;
  loading?: boolean;
}

interface IconStyledProps extends MUIStyledCommonProps {
  ownerState?: {
    label?: string;
    children?: ReactNode;
  };
}

const StartIconStyled = styled('i')<IconStyledProps>(({ ownerState }) => ({
  ...((ownerState?.label || ownerState?.children) && { marginRight: '5px' }),
}));

const EndIconStyled = styled('i')<IconStyledProps>(({ ownerState }) => ({
  ...((ownerState?.label || ownerState?.children) && { marginLeft: '5px' }),
}));

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    type = 'primary',
    size = 'medium',
    label,
    children,
    startIconName,
    startIconSize = '16',
    startIconNode,
    endIconName,
    endIconSize = '16',
    endIconNode,
    onClick,
    disabled,
    styles,
    className,
    loading,
    ...props
  },
  ref
) {
  const startIcon = () => {
    if (!startIconName && !startIconNode) return;

    if (startIconNode) return startIconNode;

    if (!startIconName) return;

    return (
      <StartIconStyled
        className={`icon-${startIconSize}-${startIconName}`}
        ownerState={{
          label,
          children,
        }}
      />
    );
  };

  const endIcon = () => {
    if (!endIconName && !endIconNode) return;

    if (endIconNode) return endIconNode;

    if (!endIconName) return;

    return (
      <EndIconStyled
        className={`icon-${endIconSize}-${endIconName}`}
        ownerState={{
          label,
          children,
        }}
      />
    );
  };

  const handleClick = () => {
    onClick && onClick();
  };

  return (
    <BaseButton
      {...props}
      disabled={disabled}
      ref={ref}
      slotProps={{
        root: {
          className: clsx(`c-btn c-btn--${type} c-btn--${size}`, className),
          style: styles,
        },
      }}
      onClick={handleClick}
    >
      {startIcon()}
      {children || label}
      {endIcon()}
    </BaseButton>
  );
});

export { Button };
